import { PasswordInput } from "@/components/PasswordInput";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { PublicStats } from "@/components/PublicStats";
import { Sux1Logo } from "@/components/Sux1Logo";
import { trackSignUp } from "@/lib/analytics";
import { AuthHeroPanel } from "@/pages/Login";


export default function Signup() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await register(name.trim(), email.trim(), password);
      trackSignUp("email");
      toast.success(t("auth.account_created"));
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.signup_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-12">
      <div className="lg:col-span-5 flex flex-col p-8 lg:p-16 border-r border-border">
        <div className="flex items-center justify-between mb-16">
          <Link to="/" className="flex items-center">
            <Sux1Logo size="lg" />
          </Link>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </div>

        <div className="max-w-md w-full mx-auto flex-1 flex flex-col justify-center">
          <div className="sux-marker mb-3">{t("auth.section_new")}</div>
          <h1 className="text-4xl sm:text-5xl font-black mb-4 leading-[0.95]">
            {t("auth.signup_title")}
          </h1>
          <p className="text-muted-foreground mb-10 text-sm">
            {t("auth.signup_subtitle")}
          </p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="sux-marker block mb-2">{t("auth.full_name")}</label>
              <input type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auth.name_placeholder")} data-testid={AUTH.signupName} className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]" />
            </div>
            <div>
              <label className="sux-marker block mb-2">{t("auth.email")}</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.email_placeholder")} data-testid={AUTH.signupEmail} className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]" />
            </div>
            <div>
              <label className="sux-marker block mb-2">{t("auth.password_min")}</label>
              <PasswordInput required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" testid={AUTH.signupPassword} className="w-full bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]" />
            </div>
            <button type="submit" disabled={submitting} data-testid={AUTH.signupSubmit} className="w-full bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-xs py-4 hover:bg-[#00227A] transition-colors disabled:opacity-50">
              {submitting ? t("auth.creating") : t("auth.signup_btn")}
            </button>
          </form>

          <Link
            to="/login"
            data-testid={AUTH.signupToLogin}
            className="mt-10 pt-6 border-t border-border flex items-center justify-between hover:opacity-75 transition-opacity"
          >
            <span className="sux-marker">{t("auth.already_member")}</span>
            <span className="font-mono uppercase tracking-[0.2em] text-[11px] underline underline-offset-4 decoration-[#FF2A00] hover:text-[#002FA7]">
              {t("auth.signin_link")}
            </span>
          </Link>

          <PublicStats />
        </div>

        <div className="mt-16 pt-6 border-t border-border flex flex-wrap items-center gap-x-6 gap-y-3">
          <span className="sux-marker">{t("auth.footer")}</span>
          <span className="ml-auto flex items-center gap-4">
            <Link
              to="/about"
              data-testid="signup-about-link"
              className="font-mono uppercase tracking-[0.2em] text-[11px] text-muted-foreground hover:text-[#002FA7] transition-colors"
            >
              {t("nav.about")}
            </Link>
            <Link
              to="/privacy"
              data-testid="signup-privacy-link"
              className="font-mono uppercase tracking-[0.2em] text-[11px] text-muted-foreground hover:text-[#002FA7] transition-colors"
            >
              {t("nav.privacy")}
            </Link>
            <a
              href="mailto:fiestamax@gmail.com?subject=%5BSUX1%5D%20Contact"
              data-testid="signup-contact-link"
              className="font-mono uppercase tracking-[0.2em] text-[11px] text-muted-foreground hover:text-[#002FA7] transition-colors"
            >
              Contact
            </a>
          </span>
        </div>
      </div>

      <AuthHeroPanel />
    </div>
  );
}
